/* eslint-disable max-statements */
import loadScript from '../../utils/loadScript';

import logger from '../../config/app.logger';
import { getExtendedLogger } from '../Logger';

import type {
  TFreshchat,
  TFreshchatConfig,
  TFreshchatOptions,
  TFreshchatEventHandlers,
} from './types';

import {
  FRESHCHAT_HOST,
  FRESHCHAT_WIDGET_URL,
  FRESHCHAT_WIDGET_CLASS,
  LOGGER_NAME,
  FRESHCHAT_EVENT_NAME,
  MESSAGES,
  FRESHCHAT_WIDGET_ID_FOR_UX_REFRESH,
} from './constants';

import { getFreshchatWidgetStyle } from './style';

declare var fcWidget: TFreshchat;

const log = getExtendedLogger(LOGGER_NAME, logger);

const noop = () => null;

function checkFreshchatUserPresent() {
  return new Promise(resolve => {
    fcWidget.user.get((response: { status?: number }) => resolve(response.status !== 200));
  });
}

function createFreshchatUser(config: TFreshchatConfig) {
  const { name, email, userId, companyId, learningSite } = config;
  fcWidget.user.setFirstName(name || userId);
  fcWidget.user.setEmail(email);
  fcWidget.user.setProperties({
    cname: companyId,
    lsLink: learningSite,
  });
}

function prepareOptionsFromConfig(config: TFreshchatConfig) {
  const {
    id: freshchatId,
    restoreId: freshchatRestoreId,
    userId,
    companyId,
    isBerryChatEnabled,
    widgetUuid,
  } = config;
  const fcConfig = {
    token: freshchatId,
    host: FRESHCHAT_HOST,
    siteId: companyId,
    externalId: `${userId}|${companyId}`,
    restoreId: freshchatRestoreId ? freshchatRestoreId : null,
    config: {
      cssNames: {
        widget: FRESHCHAT_WIDGET_CLASS,
      },
      headerProperty: {
        hideChatButton: isBerryChatEnabled,
      },
    },
    widgetUuid: FRESHCHAT_WIDGET_ID_FOR_UX_REFRESH,
  } as TFreshchatOptions;

  if (isBerryChatEnabled) {
    fcConfig.config.content = {
      placeholders: {
        reply_field: MESSAGES.REPLY_FIELD_PLACEHOLDER,
        csat_reply: MESSAGES.CSAT_REPLY_PLACEHOLDER,
      },
      headers: {
        csat_question: MESSAGES.CSAT_QUESTION,
      },
    };
  }

  if (widgetUuid) {
    fcConfig.widgetUuid = widgetUuid;
  }

  return fcConfig;
}

function attachEventHandlers(
  {
    onUserCreation = noop,
    onCsatReceived = noop,
    onCsatUpdated = noop,
    onMessageSent = noop,
    onMessageReceived = noop,
    onResolve = noop,
    onWidgetClosed = noop,
    onWidgetLoaded = noop,
  }: TFreshchatEventHandlers,
  isBerryChatEnabled: boolean
) {
  fcWidget.on(FRESHCHAT_EVENT_NAME.WIDGET_USER_CREATED, onUserCreation);
  fcWidget.on(FRESHCHAT_EVENT_NAME.WIDGET_LOADED, onWidgetLoaded);

  if (isBerryChatEnabled) {
    const eventHandlersConfig = {
      [FRESHCHAT_EVENT_NAME.WIDGET_CLOSED]: onWidgetClosed,
      [FRESHCHAT_EVENT_NAME.WIDGET_CSAT_RECEIVED]: onCsatReceived,
      [FRESHCHAT_EVENT_NAME.WIDGET_CSAT_UPDATED]: onCsatUpdated,
      [FRESHCHAT_EVENT_NAME.WIDGET_CONVERSATION_RESOLVED]: onResolve,
      [FRESHCHAT_EVENT_NAME.WIDGET_MESSAGE_SENT]: onMessageSent,
      [FRESHCHAT_EVENT_NAME.WIDGET_MESSAGE_RECEIVED]: onMessageReceived,
    };

    Object.entries(eventHandlersConfig).forEach(([eventName, eventHandler]) => {
      fcWidget.on(eventName, eventHandler);
    });
  }
}

async function loadFreshchat(
  config: TFreshchatConfig,
  eventHandlers: TFreshchatEventHandlers = {}
) {
  const { id: freshchatId } = config;

  if (!freshchatId) {
    log('id(freshchatId) not present, skipping loading phase');
    return;
  }

  log('loading with id %s', freshchatId);

  await loadScript({
    url: FRESHCHAT_WIDGET_URL,
    defer: true,
  });

  if (!fcWidget) {
    log('not loaded, skipping initialisation phase');
    return;
  }

  const options = prepareOptionsFromConfig(config);
  log('initialising with options %o', options);
  fcWidget.init(options);

  const { isBerryChatEnabled } = config;
  if (!eventHandlers) {
    log('no eventhandlers provided, skipping event handlers phase');
  } else {
    attachEventHandlers(eventHandlers, isBerryChatEnabled);
  }

  log('checking user existance');
  const isAvailable = await checkFreshchatUserPresent();

  if (!isAvailable) {
    log('user not available, creating user');

    createFreshchatUser(config);
  } else {
    log('user available');
  }
}

const clearFreshchatUser = () => {
  try {
    log('clearing user');
    fcWidget.user.clear();
    log('successfully cleaned');
  } catch (error) {
    log('clearing user failed');
    console.error(error); //eslint-disable-line no-console
  }
};

const openChatbox = () => {
  try {
    fcWidget.open();
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

const closeChatbox = () => {
  try {
    fcWidget.close();
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

const toggleFreshchat = ({
  hideButton,
  hideChatbox = true,
}: {
  hideButton: boolean;
  hideChatbox: boolean;
}) => {
  try {
    fcWidget?.setConfig({ headerProperty: { hideChatButton: hideButton } });
    !hideChatbox && openChatbox();
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

const toggleFreshchatWidget = () => {
  try {
    fcWidget?.isOpen() ? closeChatbox() : openChatbox();
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

const updateFreshchatProperties = (data: { berryAdminChatUrl?: string; learningSite?: string }) => {
  try {
    fcWidget?.conversation.setConversationProperties({
      cf_berry_chat_link: data?.berryAdminChatUrl,
      cf_lslink: data?.learningSite || window.location.hostname,
    });
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

const isFreshchatInitialized = () => fcWidget?.isInitialized();

const isFreshchatWidgetOpen = () => fcWidget?.isOpen();

export {
  closeChatbox,
  loadFreshchat,
  clearFreshchatUser,
  openChatbox,
  toggleFreshchat,
  updateFreshchatProperties,
  isFreshchatInitialized,
  getFreshchatWidgetStyle,
  toggleFreshchatWidget,
  isFreshchatWidgetOpen,
};
